import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { AppShell } from "@/components/AppShell";
import { Alert } from "@/components/ui/Alert";
import { Button } from "@/components/ui/Button";
import { Dialog } from "@/components/ui/Dialog";
import { Checkbox, SelectInput, TextInput } from "@/components/ui/Field";
import { Spinner } from "@/components/ui/Spinner";
import { ApiError } from "@/lib/apiClient";
import { AUSWERTUNGEN_SECTION_SUBTITLES } from "@/lib/auswertungenNav";
import {
  currentMonthIso,
  fetchHeroExpectedHoursByDay,
  fetchHeroUsageReport,
  filterHeroUsageRows,
  formatDateDe,
  formatDurationHours,
  formatMonthLabelDe,
  getAssignmentState,
  getCachedHeroUsageReport,
  monthDateRange,
  shiftMonthIso,
  type HeroUsageRow,
  type HeroUsageStatusFilter,
} from "@/lib/auswertungen";
import { useRealtime } from "@/lib/useRealtime";
import { HeroUsageTable } from "@/pages/auswertungen/HeroUsageTable";
import { SollIstChart } from "@/pages/auswertungen/SollIstChart";
import { UsageCalendar } from "@/pages/auswertungen/UsageCalendar";
import "./auswertungen.css";

const STATUS_OPTIONS: { value: HeroUsageStatusFilter; label: string }[] = [
  { value: "all", label: "Alle" },
  { value: "assigned", label: "Stundennachweis zugeordnet" },
  { value: "unassigned", label: "kein Stundennachweis" },
  { value: "unmapped", label: "kein Stundennachweis (kein Projektmapping)" },
  { value: "festpreis", label: "Festpreis" },
];

export function HeroUsageReportPage() {
  const initialMonth = useMemo(currentMonthIso, []);
  const initialRange = useMemo(() => monthDateRange(initialMonth), [initialMonth]);

  const [month, setMonth] = useState(initialMonth);
  const [person, setPerson] = useState("");
  const [status, setStatus] = useState<HeroUsageStatusFilter>("all");
  const [hideFullLicense, setHideFullLicense] = useState(true);
  const [selectedDay, setSelectedDay] = useState<string | null>(null);

  const { from, to } = useMemo(() => monthDateRange(month), [month]);

  const [rows, setRows] = useState<HeroUsageRow[] | null>(
    () => getCachedHeroUsageReport(initialRange.from, initialRange.to)?.rows ?? null,
  );
  const [expectedHoursByDay, setExpectedHoursByDay] = useState<Map<string, number> | null>(
    null,
  );
  const [loading, setLoading] = useState(rows === null);
  const [refreshing, setRefreshing] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const loadRunRef = useRef(0);
  const expectedRunRef = useRef(0);
  const isMountedRef = useRef(true);

  useEffect(() => {
    isMountedRef.current = true;
    return () => {
      isMountedRef.current = false;
    };
  }, []);

  const load = useCallback(
    async (opts?: { background?: boolean; force?: boolean }) => {
      const runId = ++loadRunRef.current;
      const cached = getCachedHeroUsageReport(from, to);

      if (cached) {
        setRows(cached.rows);
        setLoading(false);
        if (cached.isFresh && !opts?.force) return;
      }

      if (cached || opts?.background) {
        setRefreshing(true);
      } else {
        setLoading(true);
      }

      try {
        const data = await fetchHeroUsageReport(from, to);
        if (!isMountedRef.current || runId !== loadRunRef.current) return;
        setRows(data);
        setError(null);
      } catch (err) {
        if (!isMountedRef.current || runId !== loadRunRef.current) return;
        setError(
          err instanceof ApiError
            ? err.message
            : "Auswertung konnte nicht geladen werden.",
        );
      } finally {
        if (isMountedRef.current && runId === loadRunRef.current) {
          setLoading(false);
          setRefreshing(false);
        }
      }
    },
    [from, to],
  );

  const loadExpectedHours = useCallback(async () => {
    const runId = ++expectedRunRef.current;
    try {
      const map = await fetchHeroExpectedHoursByDay(from, to);
      if (!isMountedRef.current || runId !== expectedRunRef.current) return;
      setExpectedHoursByDay(map);
    } catch {
      if (!isMountedRef.current || runId !== expectedRunRef.current) return;
      setExpectedHoursByDay(null);
    }
  }, [from, to]);

  useEffect(() => {
    void load();
  }, [load]);

  useEffect(() => {
    void loadExpectedHours();
  }, [loadExpectedHours]);

  useRealtime({
    scopes: ["domain:timesheets"],
    domains: ["timesheets"],
    events: ["timesheet_hero_usage_updated"],
    onChange: () => {
      void load({ background: true, force: true });
      void loadExpectedHours();
    },
  });

  const { filtered, totals } = useMemo(
    () => filterHeroUsageRows(rows ?? [], { person, status, hideFullLicense }),
    [rows, person, status, hideFullLicense],
  );

  const onlyUnmapped = useMemo(() => {
    if (!rows || rows.length === 0) return false;
    return rows.every((row) => getAssignmentState(row) === "unmapped");
  }, [rows]);

  const hasPartialData = useMemo(
    () => Boolean(rows?.some((row) => row.partial_data)),
    [rows],
  );

  const dayRows = useMemo(
    () =>
      selectedDay
        ? filtered.filter(
            (row) => String(row.work_date ?? "").trim().slice(0, 10) === selectedDay,
          )
        : [],
    [filtered, selectedDay],
  );

  const initialLoading = loading && rows === null;
  const isCurrentMonth = month === currentMonthIso();

  return (
    <AppShell
      title="Auswertungen"
      subtitle={AUSWERTUNGEN_SECTION_SUBTITLES["hero-usage"]}
      actions={
        <div className="flex items-center gap-2">
          {refreshing && (
            <span className="flex items-center gap-1.5 text-xs text-label-secondary">
              <Spinner className="h-3.5 w-3.5" />
              wird aktualisiert …
            </span>
          )}
          <Button
            variant="secondary"
            size="sm"
            onClick={() => void load({ force: true })}
            loading={loading || refreshing}
          >
            Neu laden
          </Button>
        </div>
      }
    >
      {error && (
        <Alert variant="error" title="Auswertung konnte nicht geladen werden">
          {error}
        </Alert>
      )}
      {hasPartialData && (
        <Alert variant="warning" title="Unvollständige Daten">
          Hero konnte nicht für alle Projekte abgefragt werden. Die Auswertung ist
          möglicherweise unvollständig.
        </Alert>
      )}

      <section className="rounded-ios-lg bg-card px-4 py-4 shadow-ios-md ring-1 ring-separator/35">
        <div className="flex flex-wrap items-center justify-between gap-3">
          <div className="flex flex-wrap items-center gap-2">
            <button
              type="button"
              className="inline-flex h-9 w-9 shrink-0 items-center justify-center rounded-lg border border-separator text-lg text-label transition hover:border-accent hover:text-accent"
              aria-label="Vorheriger Monat"
              onClick={() => setMonth((current) => shiftMonthIso(current, -1))}
            >
              ‹
            </button>
            <div className="min-w-[10rem] text-center text-base font-semibold capitalize text-label">
              {formatMonthLabelDe(month)}
            </div>
            <button
              type="button"
              className="inline-flex h-9 w-9 shrink-0 items-center justify-center rounded-lg border border-separator text-lg text-label transition hover:border-accent hover:text-accent"
              aria-label="Nächster Monat"
              onClick={() => setMonth((current) => shiftMonthIso(current, 1))}
            >
              ›
            </button>
            {!isCurrentMonth ? (
              <Button
                type="button"
                variant="secondary"
                size="sm"
                className="h-8"
                onClick={() => setMonth(currentMonthIso())}
              >
                Aktueller Monat
              </Button>
            ) : null}
          </div>
        </div>

        <div className="mt-4 grid grid-cols-1 gap-3 sm:grid-cols-2">
          <TextInput
            label="Person"
            placeholder="Name oder Partner"
            value={person}
            onChange={(e) => setPerson(e.target.value)}
          />
          <SelectInput
            label="Status"
            value={status}
            onChange={(e) => setStatus(e.target.value as HeroUsageStatusFilter)}
          >
            {STATUS_OPTIONS.map((opt) => (
              <option key={opt.value} value={opt.value}>
                {opt.label}
              </option>
            ))}
          </SelectInput>
        </div>
        <div className="mt-3 flex flex-wrap items-center justify-between gap-3">
          <Checkbox
            label="Hero Lizenz Full ausblenden"
            checked={hideFullLicense}
            onChange={(e) => setHideFullLicense(e.target.checked)}
          />
          <p className="text-xs text-label-secondary">
            Person, Status und Lizenz-Filter wirken sofort – ohne Neuladen.
          </p>
        </div>
      </section>

      {initialLoading ? (
        <div className="flex items-center justify-center py-16 text-label-secondary">
          <Spinner />
          <span className="ml-2 text-sm">Auswertung wird geladen …</span>
        </div>
      ) : (
        <>
          <section className="flex flex-wrap items-center gap-x-4 gap-y-1 text-sm text-label-secondary">
            <span className="font-medium text-label">
              {filtered.length ? `${filtered.length} Einträge` : "Keine Einträge"}
            </span>
            <span>Mit Stundennachweis: {formatDurationHours(totals.assignedSeconds)}</span>
            <span>Ohne Stundennachweis: {formatDurationHours(totals.unassignedSeconds)}</span>
            <span>
              Ohne Stundennachweis (mit Projektmapping):{" "}
              {formatDurationHours(totals.unassignedMappedSeconds)}
            </span>
            <span>
              Ohne Stundennachweis (kein Projektmapping):{" "}
              {formatDurationHours(totals.unmappedSeconds)}
            </span>
            <span>Teilweise Stundennachweis: {formatDurationHours(totals.partialSeconds)}</span>
            <span>Festpreis: {formatDurationHours(totals.festpreisSeconds)}</span>
          </section>

          {onlyUnmapped && (
            <p className="text-sm text-label-secondary">
              Hinweis: Es wurden nur nicht zugeordnete Hero-Zeiteinträge gefunden (kein
              lokales Projekt-Mapping).
            </p>
          )}

          <UsageCalendar
            rows={filtered}
            from={from}
            to={to}
            hideMonthTitle
            expectedHoursByDay={expectedHoursByDay}
            onSelectDay={(isoDate) => setSelectedDay(isoDate)}
          />
          <SollIstChart
            rows={filtered}
            from={from}
            to={to}
            expectedHoursByDay={expectedHoursByDay}
          />
        </>
      )}

      <Dialog
        open={selectedDay !== null}
        onClose={() => setSelectedDay(null)}
        title={selectedDay ? `Details für ${formatDateDe(selectedDay)}` : ""}
        size="4xl"
      >
        <HeroUsageTable
          rows={dayRows}
          emptyText="Keine Einträge für diesen Tag."
          stickyHeader={false}
        />
      </Dialog>
    </AppShell>
  );
}
